import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserSidebarLayout } from '../components/UserSidebarLayout';
import { COLORS } from '../../../core/constants/colors';
import { api } from '../../../core/utils/apiClient';
import { CompanyMembersManager } from '../components/CompanyMembersManager';
import { EditCompanyModal } from '../../companies/components/EditCompanyModal';

interface UserCompany {
  id: number;
  company_id: string;
  name: string;
  website?: string;
  industry?: string;
  location?: string;
  founding_year?: number;
  employees?: string;
  description?: string;
  domain?: string;
  is_private: boolean;
  profile_completeness_score: number;
  created_at: string;
  owner_id: string;
  enrichment_score?: number;
  last_enrichment_at?: string;
  enriched_data?: string;
  pending_verification: boolean;
  updated_at: string;
  created_by: string;
  updated_by: string;
  is_active: boolean;
  user_role: string;
}

export const UserCompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<UserCompany[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<UserCompany | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMembersManager, setShowMembersManager] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const isCompanyAdmin = (company: UserCompany) =>
    company.user_role === 'admin' || company.user_role === 'owner';

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    // Auto-select first company when companies are loaded
    if (companies.length > 0 && !selectedCompany) {
      setSelectedCompany(companies[0]);
    }
  }, [companies, selectedCompany]);

  const loadCompanies = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get('/api/v1/companies/my-companies');

      if (response.ok) {
        const data = await response.json();
        const companiesList = Array.isArray(data) ? data : (data.companies || []);
        setCompanies(companiesList);
      } else if (response.status === 404) {
        setCompanies([]);
      } else {
        setError('Failed to load companies');
      }
    } catch (err: any) {
      console.error('Error loading companies:', err);
      setError(err.message || 'Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  };

  const getCompanyInitial = (name: string) => {
    return name?.charAt(0).toUpperCase() || 'C';
  };

  const getInitialColor = (name: string) => {
    const colors = [
      '#3B82F6', '#10B981', '#F59E0B', '#EF4444', 
      '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'
    ];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const formatWebsiteUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  if (isLoading) {
    return (
      <UserSidebarLayout>
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div
            className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
            style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
          />
        </div>
      </UserSidebarLayout>
    );
  }

  if (error) {
    return (
      <UserSidebarLayout>
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-500 text-lg mb-4">{error}</p>
            <button
              onClick={loadCompanies}
              className="px-6 py-2 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
              style={{ backgroundColor: COLORS.user.primary }}
            >
              Retry
            </button>
          </div>
        </div>
      </UserSidebarLayout>
    );
  }

  return (
    <UserSidebarLayout>
      <div className="min-h-screen bg-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        {/* Header Section */}
        <div className="bg-white border-b" style={{ borderColor: '#E5E7EB' }}>
          <div className="max-w-7xl mx-auto px-6 py-6">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <button onClick={() => navigate('/dashboard')} className="hover:underline">
                Dashboard
              </button>
              <span>›</span>
              <span className="text-gray-900 font-medium">My Companies</span>
            </div>

            <h1 className="text-3xl font-bold text-gray-900">My Companies</h1>
            <p className="text-sm text-gray-600 mt-1">
              {companies.length} {companies.length === 1 ? 'company' : 'companies'}
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          {companies.length === 0 ? (
            <div className="bg-white rounded-lg border p-12 text-center" style={{ borderColor: '#E5E7EB' }}>
              <p className="text-gray-400">No companies found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Companies List */}
              <div className="lg:col-span-1">
                <div className="space-y-3">
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => setSelectedCompany(company)}
                      className={`w-full p-4 rounded-lg border text-left transition-all ${
                        selectedCompany?.id === company.id
                          ? 'bg-blue-50'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                      style={{
                        borderColor: selectedCompany?.id === company.id ? COLORS.user.primary : '#E5E7EB',
                        borderWidth: selectedCompany?.id === company.id ? '2px' : '1px'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-lg font-bold flex-shrink-0"
                          style={{ backgroundColor: getInitialColor(company.name) }}
                        >
                          {getCompanyInitial(company.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate text-sm">
                            {company.name}
                          </h3>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {company.industry || 'No industry'}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span 
                              className="text-xs px-2 py-0.5 rounded-full font-medium capitalize"
                              style={{ 
                                backgroundColor: company.user_role === 'owner' ? '#DBEAFE' : '#F3F4F6',
                                color: company.user_role === 'owner' ? '#1E40AF' : '#6B7280'
                              }}
                            >
                              {company.user_role}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Right Column - Company Details */}
              <div className="lg:col-span-2">
                {selectedCompany ? (
                  <div>
                    {/* Company Header */}
                    <div className="bg-white rounded-lg border p-6 mb-6" style={{ borderColor: '#E5E7EB' }}>
                      <div className="flex items-start gap-4 mb-6">
                        <div
                          className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold flex-shrink-0"
                          style={{ backgroundColor: getInitialColor(selectedCompany.name) }}
                        >
                          {getCompanyInitial(selectedCompany.name)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                {selectedCompany.name}
                              </h2>
                              <p className="text-sm text-gray-600">
                                {selectedCompany.industry || 'Industry'} · {selectedCompany.location || 'Location'} · {selectedCompany.employees || 'N/A'} Employees
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Overview Metrics */}
                      <div className="grid grid-cols-2 gap-4">
                        {selectedCompany.location && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Headquarters</p>
                            <p className="text-sm text-gray-900">{selectedCompany.location}</p>
                          </div>
                        )}
                        {selectedCompany.website && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Website</p>
                            <a
                              href={formatWebsiteUrl(selectedCompany.website)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm hover:underline"
                              style={{ color: COLORS.user.primary }}
                            >
                              {selectedCompany.website}
                            </a>
                          </div>
                        )}
                        {selectedCompany.founding_year && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Founded</p>
                            <p className="text-sm text-gray-900">{selectedCompany.founding_year}</p>
                          </div>
                        )}
                        {selectedCompany.employees && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Employees</p>
                            <p className="text-sm text-gray-900">{selectedCompany.employees}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Role</p>
                          <p className="text-sm text-gray-900 capitalize">{selectedCompany.user_role}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Status</p>
                          <span 
                            className="text-sm font-medium"
                            style={{ color: selectedCompany.is_active ? '#10B981' : '#EF4444' }}
                          >
                            {selectedCompany.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>

                      {/* Industry Tags */}
                      {selectedCompany.industry && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {selectedCompany.industry.split(',').map((tag, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                            >
                              {tag.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* About Section */}
                    <div className="bg-white rounded-lg border p-6 mb-6" style={{ borderColor: '#E5E7EB' }}>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">About {selectedCompany.name}</h3>
                      {selectedCompany.description ? (
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {selectedCompany.description}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No description available for this company.</p>
                      )}
                    </div>

                    {/* Company Stats */}
                    <div className="bg-white rounded-lg border p-6 mb-6" style={{ borderColor: '#E5E7EB' }}>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Company Statistics</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Profile Completeness</p>
                          <p className="text-2xl font-bold" style={{ color: COLORS.user.primary }}>
                            {selectedCompany.profile_completeness_score || 0}%
                          </p>
                        </div>
                        {selectedCompany.enrichment_score !== null && selectedCompany.enrichment_score !== undefined && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Enrichment Score</p>
                            <p className="text-2xl font-bold" style={{ color: COLORS.user.primary }}>
                              {selectedCompany.enrichment_score}%
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Privacy</p>
                          <p className="text-sm font-semibold text-gray-900 mt-1">
                            {selectedCompany.is_private ? 'Private Company' : 'Public Company'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => navigate(`/dashboard/companies/${selectedCompany.id}`)}
                        className="px-6 py-2.5 rounded-lg text-white font-medium text-sm transition-opacity hover:opacity-90"
                        style={{ backgroundColor: COLORS.user.primary }}
                      >
                        View Full Profile
                      </button>
                      <button
                        onClick={() => setShowMembersManager(true)}
                        className="px-6 py-2.5 rounded-lg font-medium text-sm border transition-colors hover:bg-gray-50"
                        style={{ borderColor: COLORS.user.primary, color: COLORS.user.primary }}
                      >
                        Manage Members
                      </button>
                      {isCompanyAdmin(selectedCompany) && (
                        <button
                          onClick={() => setShowEditModal(true)}
                          className="px-6 py-2.5 rounded-lg font-medium text-sm border transition-colors hover:bg-gray-50"
                          style={{ borderColor: '#E5E7EB', color: '#374151' }}
                        >
                          Edit Company
                        </button>
                      )}
                      {selectedCompany.website && (
                        <button
                          onClick={() => window.open(formatWebsiteUrl(selectedCompany.website!), '_blank')}
                          className="px-6 py-2.5 rounded-lg font-medium text-sm border transition-colors hover:bg-gray-50"
                          style={{ borderColor: '#E5E7EB', color: '#374151' }}
                        >
                          Visit Website
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border p-12 text-center" style={{ borderColor: '#E5E7EB' }}>
                    <p className="text-gray-400">Select a company to view details</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Members Manager Modal */}
      {showMembersManager && selectedCompany && (
        <CompanyMembersManager
          companyId={selectedCompany.id}
          currentUserRole={selectedCompany.user_role}
          onClose={() => setShowMembersManager(false)}
        />
      )}

      {/* Edit Company Modal — admin/owner only */}
      {showEditModal && selectedCompany && (
        <EditCompanyModal
          company={selectedCompany}
          onClose={() => setShowEditModal(false)}
          onSaved={(updated) => {
            setSelectedCompany({ ...selectedCompany, ...updated });
            setCompanies((prev) =>
              prev.map((c) => (c.id === selectedCompany.id ? { ...c, ...updated } : c))
            );
          }}
          accentColor={COLORS.user.primary}
        />
      )}
    </UserSidebarLayout>
  );
};
